/* eslint-disable */
// src/pages/dashboard/index.tsx
import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
  SyntheticEvent,
} from "react";
import {
  Box,
  Grid,
  Container,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { usePageStore } from "@/hooks/usePageStore";
import { useTranslation } from "react-i18next";
import HeaderFilters, { Filters } from "./components/headerFilters";
import ApprovalRequest from "./components/approvalRequest";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetSettings, useDisplayDashboard } from "@/hooks/useSetting";
import SkeletonLoader from "@/components/SkeletonLoader";
import { useAuditLogFirstRender } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useGetUserPrograms } from "@/hooks/usePrograms";

const { QlikEmbed } = require("@qlik/embed-react");

interface CardConfig {
  action: string;
  cardId?: string;
  title?: string;
}

interface SectionConfig {
  action: string;
  title: string;
  cards: CardConfig[];
}

const DashboardPage: FC = () => {
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("common");
  const { userInfo, userHasAccessToCode, userIsAdminOrSupport } =
    useUserStore();
  const displayDashboard = useDisplayDashboard();
  const [filters, setFilters] = useState<Filters>({
    sortField: "fullName",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 10,
    search: "",
    status: ["Active", "Completed"],
    programId: "",
    orgUnitId: "",
    orgGroupLabels: [],
    excludeFromProgramsAndOrgUnits: false,
    excludeProgramIds: [],
  });
  const { programs } = useGetUserPrograms({
    userId: userInfo?._id as string,
    sortType: "asc",
    hasSystemAdminRole: userIsAdminOrSupport(),
  });

  const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";

  const { data: settings, isLoading: isLoadingSettings } = useGetSettings();

  // Fix 1: Capture the Qlik API reference only once using a ref.
  // Multiple QlikEmbed components call exposeRefApi, but we only need
  // one stable reference since they all share the same Qlik app instance.
  const qlikApiRef = useRef<any>(null);
  const [qlikReady, setQlikReady] = useState(false);

  const handleQlikApiReady = useCallback((api: any) => {
    if (qlikApiRef.current) {
      console.log("[Qlik] exposeRefApi called again, ignoring (already captured)");
      return;
    }
    console.log("[Qlik] API reference captured:", api);
    qlikApiRef.current = api;
    setQlikReady(true);
  }, []);

  useEffect(() => {
    setPageTitle(t("home"));
  }, [t, setPageTitle]);

  // Fix 3: Debounce timer ref to prevent cascading filter applications
  // (e.g., program change resets orgUnitId → auto-select fills it back)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fix 4: Extract only Qlik-relevant fields so unrelated filter changes
  // (pageNumber, search, sortField) don't re-trigger Qlik updates
  const qlikProgramId = filters.programId;
  const qlikOrgUnitId = filters.orgUnitId;
  const qlikOrgGroupLabels = filters.orgGroupLabels;

  useEffect(() => {
    console.log("[Qlik] useEffect triggered:", {
      qlikReady,
      qlikProgramId,
      qlikOrgUnitId,
      qlikOrgGroupLabels,
    });

    if (!qlikReady || !qlikApiRef.current) {
      console.log("[Qlik] Skipping: API not ready yet");
      return;
    }
    if (!qlikProgramId) {
      console.log("[Qlik] Skipping: No program selected");
      return;
    }

    // Clear any pending debounced call
    if (debounceRef.current) {
      console.log("[Qlik] Debounce: Cancelling previous pending call");
      clearTimeout(debounceRef.current);
    }

    console.log("[Qlik] Debounce: Scheduling filter application in 300ms");

    debounceRef.current = setTimeout(async () => {
      console.log("[Qlik] Debounce: Executing filter application now");

      const hasLabels = qlikOrgGroupLabels && qlikOrgGroupLabels.length > 0;
      const orgUnitForQlik = qlikOrgUnitId ? qlikOrgUnitId.replaceAll(",", "|") : "";
      const labelsForQlik = hasLabels ? qlikOrgGroupLabels.join("|") : "";

      console.log("[Qlik] ═══ FILTER COMPARISON ═══");
      console.log("[Qlik] Has labels selected?", hasLabels);
      console.log("[Qlik]");
      console.log("[Qlik] WITHOUT labels (program + org only):");
      console.log("[Qlik]   vSelectedProgram =", qlikProgramId);
      console.log("[Qlik]   vSelectedOU      =", orgUnitForQlik);
      console.log("[Qlik]   vSelectedOULabel =", "(empty)");
      console.log("[Qlik]");
      console.log("[Qlik] WITH labels (what we are sending now):");
      console.log("[Qlik]   vSelectedProgram =", qlikProgramId);
      console.log("[Qlik]   vSelectedOU      =", orgUnitForQlik);
      console.log("[Qlik]   vSelectedOULabel =", hasLabels ? labelsForQlik : "(empty)");
      console.log("[Qlik]");
      console.log("[Qlik] DIFF when labels selected:");
      console.log("[Qlik]   vSelectedOULabel changed: '' → '" + labelsForQlik + "'");
      console.log("[Qlik]   vSelectedOU changed?", "Check if OUs were filtered by label selection");
      console.log("[Qlik]   orgUnitId raw value =", qlikOrgUnitId);
      console.log("[Qlik]   orgGroupLabels raw value =", qlikOrgGroupLabels);
      console.log("[Qlik] ═══ END COMPARISON ═══");

      try {
        const doc = await qlikApiRef.current.getDoc();
        const { app } = doc;
        console.log("[Qlik] Got Qlik doc:", doc);

        // ── Diagnostic: Inspect Qlik data model fields and values ──
        console.log("[Qlik] === DATA MODEL INSPECTION ===");

        // List all fields in the Qlik app
        try {
          const fieldListObj = await app.createSessionObject({
            qInfo: { qType: "FieldList" },
            qFieldListDef: { qShowSystem: false, qShowHidden: false },
          });
          const fieldListLayout = await fieldListObj.getLayout();
          const fields = fieldListLayout?.qFieldList?.qItems || [];
          console.log(
            "[Qlik] Available fields in data model:",
            fields.map((f: any) => f.qName),
          );

          // Check if our expected fields exist
          const expectedFields = ["program_id", "orgUnitId", "enrollment_id"];
          expectedFields.forEach((name) => {
            const found = fields.find((f: any) => f.qName === name);
            console.log(
              `[Qlik] Field "${name}": ${found ? "EXISTS" : "NOT FOUND"}`,
            );
          });

          // Look for similar field names (partial match)
          const programRelated = fields.filter(
            (f: any) =>
              f.qName.toLowerCase().includes("program") ||
              f.qName.toLowerCase().includes("prog"),
          );
          console.log(
            "[Qlik] Program-related fields:",
            programRelated.map((f: any) => f.qName),
          );

          const orgRelated = fields.filter(
            (f: any) =>
              f.qName.toLowerCase().includes("org") ||
              f.qName.toLowerCase().includes("unit"),
          );
          console.log(
            "[Qlik] Org-related fields:",
            orgRelated.map((f: any) => f.qName),
          );

          const incentiveRelated = fields.filter(
            (f: any) =>
              f.qName.toLowerCase().includes("incentive") ||
              f.qName.toLowerCase().includes("reward") ||
              f.qName.toLowerCase().includes("total"),
          );
          console.log(
            "[Qlik] Incentive-related fields:",
            incentiveRelated.map((f: any) => f.qName),
          );
        } catch (err) {
          console.warn("[Qlik] Could not list fields:", err);
        }

        // Inspect actual values in program_id field
        try {
          const programListObj = await app.createSessionObject({
            qInfo: { qType: "programValues" },
            qListObjectDef: {
              qDef: { qFieldDefs: ["program_id"] },
              qInitialDataFetch: [{ qLeft: 0, qTop: 0, qWidth: 1, qHeight: 50 }],
            },
          });
          const programListLayout = await programListObj.getLayout();
          const programValues =
            programListLayout?.qListObject?.qDataPages?.[0]?.qMatrix || [];
          console.log(
            "[Qlik] Values in program_id field:",
            programValues.map((row: any) => ({
              text: row[0]?.qText,
              num: row[0]?.qNum,
              state: row[0]?.qState,
            })),
          );
          console.log(
            "[Qlik] We are sending programId =",
            qlikProgramId,
            "| Match?",
            programValues.some((row: any) => row[0]?.qText === qlikProgramId),
          );
        } catch (err) {
          console.warn("[Qlik] Could not inspect program_id values:", err);
        }

        // Inspect actual values in orgUnitId field
        try {
          const orgListObj = await app.createSessionObject({
            qInfo: { qType: "orgValues" },
            qListObjectDef: {
              qDef: { qFieldDefs: ["orgUnitId"] },
              qInitialDataFetch: [{ qLeft: 0, qTop: 0, qWidth: 1, qHeight: 50 }],
            },
          });
          const orgListLayout = await orgListObj.getLayout();
          const orgValues =
            orgListLayout?.qListObject?.qDataPages?.[0]?.qMatrix || [];
          console.log(
            "[Qlik] Values in orgUnitId field:",
            orgValues.map((row: any) => ({
              text: row[0]?.qText,
              num: row[0]?.qNum,
              state: row[0]?.qState,
            })),
          );
          const sentOrgIds = qlikOrgUnitId.split(",").filter(Boolean);
          console.log(
            "[Qlik] We are sending orgUnitIds =",
            sentOrgIds,
            "| Matches?",
            sentOrgIds.map((id) => ({
              id,
              found: orgValues.some((row: any) => row[0]?.qText === id),
            })),
          );
        } catch (err) {
          console.warn("[Qlik] Could not inspect orgUnitId values:", err);
        }

        // List all variables in the app
        try {
          const varListObj = await app.createSessionObject({
            qInfo: { qType: "VariableList" },
            qVariableListDef: { qType: "variable" },
          });
          const varListLayout = await varListObj.getLayout();
          const variables = varListLayout?.qVariableList?.qItems || [];
          console.log(
            "[Qlik] Available variables:",
            variables.map((v: any) => ({
              name: v.qName,
              definition: v.qDefinition,
              description: v.qDescription,
            })),
          );
        } catch (err) {
          console.warn("[Qlik] Could not list variables:", err);
        }

        console.log("[Qlik] === END DATA MODEL INSPECTION ===");

        // Only set variables — do NOT use clearAll() or field selections
        // as they can interfere with variable-based set analysis in Qlik
        console.log("[Qlik] --- Setting variables (only) ---");

        // Set vSelectedProgram
        if (qlikProgramId) {
          try {
            const v = await app.getVariableByName("vSelectedProgram");
            console.log("[Qlik] vSelectedProgram variable object:", v);
            await v.setStringValue(qlikProgramId);
            console.log("[Qlik] vSelectedProgram SET =", qlikProgramId);
          } catch (err) {
            console.warn("[Qlik] vSelectedProgram variable not found:", err);
          }
        }

        // Set vSelectedOU (pipe-separated)
        if (qlikOrgUnitId) {
          try {
            const orgUnitValue = qlikOrgUnitId.replaceAll(",", "|");
            const v = await app.getVariableByName("vSelectedOU");
            console.log("[Qlik] vSelectedOU variable object:", v);
            await v.setStringValue(orgUnitValue);
            console.log("[Qlik] vSelectedOU SET =", orgUnitValue);
          } catch (err) {
            console.warn("[Qlik] vSelectedOU variable not found:", err);
          }
        } else {
          // No OU selected → clear the variable so Qlik shows all
          try {
            const v = await app.getVariableByName("vSelectedOU");
            await v.setStringValue("");
            console.log("[Qlik] vSelectedOU CLEARED (no OU selected)");
          } catch (err) {
            console.warn("[Qlik] vSelectedOU clear failed:", err);
          }
        }

        // Set vSelectedOULabel (pipe-separated)
        if (qlikOrgGroupLabels && qlikOrgGroupLabels.length > 0) {
          try {
            const labelValue = qlikOrgGroupLabels.join("|");
            const v = await app.getVariableByName("vSelectedOULabel");
            console.log("[Qlik] vSelectedOULabel variable object:", v);
            await v.setStringValue(labelValue);
            console.log("[Qlik] vSelectedOULabel SET =", labelValue);
          } catch (err) {
            console.warn("[Qlik] vSelectedOULabel variable not found:", err);
          }
        } else {
          // No labels selected → clear the variable
          try {
            const v = await app.getVariableByName("vSelectedOULabel");
            await v.setStringValue("");
            console.log("[Qlik] vSelectedOULabel CLEARED (no labels selected)");
          } catch (err) {
            console.warn("[Qlik] vSelectedOULabel clear failed:", err);
          }
        }

        // ── Verify: Read back variable values after setting ──
        console.log("[Qlik] --- Verifying applied variables ---");
        try {
          const vProg = await app.getVariableByName("vSelectedProgram");
          const vProgLayout = await vProg.getLayout();
          console.log("[Qlik] vSelectedProgram readback:", {
            qText: vProgLayout?.qText,
            qNum: vProgLayout?.qNum,
            qIsScriptCreated: vProgLayout?.qIsScriptCreated,
          });
        } catch (err) {
          console.warn("[Qlik] Could not read back vSelectedProgram:", err);
        }
        try {
          const vOU = await app.getVariableByName("vSelectedOU");
          const vOULayout = await vOU.getLayout();
          console.log("[Qlik] vSelectedOU readback:", {
            qText: vOULayout?.qText,
            qNum: vOULayout?.qNum,
          });
        } catch (err) {
          console.warn("[Qlik] Could not read back vSelectedOU:", err);
        }
        try {
          const vLabel = await app.getVariableByName("vSelectedOULabel");
          const vLabelLayout = await vLabel.getLayout();
          console.log("[Qlik] vSelectedOULabel readback:", {
            qText: vLabelLayout?.qText,
            qNum: vLabelLayout?.qNum,
          });
        } catch (err) {
          console.warn("[Qlik] Could not read back vSelectedOULabel:", err);
        }

        // ── Read each card's current value from Qlik ──
        console.log("[Qlik] === CARD VALUES FROM QLIK ===");
        const allCards = actionsAndCards
          .flatMap((s) => s.cards)
          .filter((c) => c.cardId);

        for (const card of allCards) {
          try {
            const model = await app.getObject(card.cardId as string);
            const layout = await model.getLayout();

            // Extract value depending on visualization type
            const hypercube = layout?.qHyperCube;
            let cardValue: any = "N/A";

            if (hypercube) {
              const measures = hypercube.qMeasureInfo || [];
              const grandTotal = hypercube.qGrandTotalRow;
              const dataPages = hypercube.qDataPages;

              if (grandTotal && grandTotal.length > 0) {
                cardValue = grandTotal.map((gt: any) => ({
                  qText: gt.qText,
                  qNum: gt.qNum,
                }));
              } else if (dataPages?.[0]?.qMatrix?.length > 0) {
                cardValue = dataPages[0].qMatrix.map((row: any) =>
                  row.map((cell: any) => ({
                    qText: cell.qText,
                    qNum: cell.qNum,
                  })),
                );
              }

              console.log(
                `[Qlik] Card "${card.title || card.cardId}" (${card.cardId}):`,
                {
                  value: cardValue,
                  measureCount: measures.length,
                  measureTitles: measures.map((m: any) => m.qFallbackTitle),
                  vizType: layout?.visualization || layout?.qInfo?.qType,
                },
              );
            } else {
              console.log(
                `[Qlik] Card "${card.title || card.cardId}" (${card.cardId}):`,
                { layout: JSON.stringify(layout).substring(0, 500) },
              );
            }
          } catch (err) {
            console.warn(
              `[Qlik] Could not read card "${card.title || card.cardId}" (${card.cardId}):`,
              err,
            );
          }
        }
        console.log("[Qlik] === END CARD VALUES ===");

        console.log("[Qlik] All variables applied successfully");
      } catch (error) {
        console.error("[Qlik] Error applying filters:", error);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [qlikProgramId, qlikOrgUnitId, qlikOrgGroupLabels, qlikReady]);

  const qlikCardsConfig = (settings?.qlikCardsConfig as unknown as {
    actionsAndCards: SectionConfig[];
  }) || { actionsAndCards: [] };
  const actionsAndCards = qlikCardsConfig.actionsAndCards;

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.DASHBOARD,
    option: OPTIONS.DASHBOARD,
    actionCode: ACTION_CODE.WEB_DASH_VIEW,
    action: "When entering",
    detail: "Viewed dashboard",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  const [selectedCmPartTab, setSelectedCmPartTab] = useState(0);
  const handleCmPartTabChange = (_: SyntheticEvent, newValue: number) => {
    setSelectedCmPartTab(newValue);
  };

  return (
    <SkeletonLoader
      isLoading={isLoadingSettings || !settings || !userInfo?._id}
      skeletonCount={3}
      skeletonHeight={40}
    >
      {userHasAccessToCode("DASHBOARD_VIEW") && (
        <Container sx={{ py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1">
              Hello, {userInfo?.firstName} {userInfo?.lastName}!
            </Typography>
          </Box>

          {displayDashboard && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="p" color="text.secondary">
                  {filters.programId
                    ? programs?.find((p) => p._id === filters.programId)?.name ||
                      `Program ID: ${filters.programId}`
                    : `Program ID: ${filters.programId}`}
                </Typography>
                <Typography variant="subtitle1" component="p" sx={{ mt: 1 }}>
                  Here's what's going on today:
                </Typography>
              </Box>

              <HeaderFilters
                filtersState={[filters, setFilters]}
                isLoadingButtons={false}
                onOpenAuditLog={() => {}}
              />

              {actionsAndCards.map((section, sectionIdx) => {
                const visibleCards = section.cards.filter((c) => {
                  return c.cardId || c.action === "APPROVAL_EVE_ALERT";
                });
                if (visibleCards.length === 0) return null;

                if (section.action === "CM_PART_ACT") {
                  const tabObjectIds = visibleCards.map((c) => c.cardId as string);

                  return (
                    <Box key={sectionIdx} sx={{ mt: 4 }}>
                      {section.title && (
                        <Typography variant="h6" gutterBottom>
                          {section.title}
                        </Typography>
                      )}

                      <Tabs
                        value={selectedCmPartTab}
                        onChange={handleCmPartTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                      >
                        {visibleCards.map((c) => (
                          <Tab key={c.cardId} label={c.title} />
                        ))}
                      </Tabs>

                      <Box sx={{ mt: 2 }}>
                        {tabObjectIds.map((objectId, idx) => (
                          <Box
                            key={objectId}
                            role="tabpanel"
                            hidden={selectedCmPartTab !== idx}
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: 400,
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              boxShadow: 1,
                              overflow: "hidden",
                            }}
                          >
                            {selectedCmPartTab === idx && (
                              <>
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    zIndex: 1,
                                    pointerEvents: "none",
                                  }}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                                <Box
                                  sx={{
                                    position: "relative",
                                    zIndex: 2,
                                    width: "100%",
                                    height: "100%",
                                  }}
                                >
                                  <QlikEmbed
                                    ui="analytics/chart"
                                    appId={APP_ID}
                                    objectId={objectId}
                                    identity={userInfo?._id}
                                    style={{ width: "100%", height: "100%" }}
                                    exposeRefApi={handleQlikApiReady}
                                  />
                                </Box>
                              </>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                }

                const hasApproval = visibleCards.some(
                  (c) => c.action === "APPROVAL_EVE_ALERT",
                );
                if (visibleCards.length === 5 && hasApproval) {
                  const approvalCard = visibleCards.find(
                    (c) => c.action === "APPROVAL_EVE_ALERT",
                  );
                  const otherCards = visibleCards.filter(
                    (c) => c.action !== "APPROVAL_EVE_ALERT",
                  );

                  const isStatusCard = (card: CardConfig) => {
                    const t = (card.title || section.title || "").toLowerCase();
                    return t.includes("participant") && t.includes("status");
                  };

                  const statusCard = otherCards.find(isStatusCard);
                  const smallCards = otherCards.filter((c) => !isStatusCard(c));

                  return (
                    <Box key={sectionIdx} sx={{ mt: 4 }}>
                      {section.title && (
                        <Typography variant="h6" gutterBottom>
                          {section.title}
                        </Typography>
                      )}

                      <Grid container spacing={2}>
                        {approvalCard && (
                          <Grid item xs={12} md={7}>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: 500,
                                bgcolor: "background.paper",
                                borderRadius: 2,
                                boxShadow: 1,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 1,
                                  pointerEvents: "none",
                                }}
                              >
                                <CircularProgress size={24} />
                              </Box>
                              <Box
                                sx={{
                                  position: "relative",
                                  zIndex: 2,
                                  height: "100%",
                                }}
                              >
                                <ApprovalRequest />
                              </Box>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={12} md={2.5}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              height: 500,
                              gap: 2,
                            }}
                          >
                            {smallCards.slice(0, 3).map((card) => (
                              <Box
                                key={card.cardId}
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  flex: 1,
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  boxShadow: 1,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    zIndex: 1,
                                    pointerEvents: "none",
                                  }}
                                >
                                  <CircularProgress size={20} />
                                </Box>
                                <Box
                                  sx={{
                                    position: "relative",
                                    zIndex: 2,
                                    height: "100%",
                                  }}
                                >
                                  <QlikEmbed
                                    ui="analytics/chart"
                                    appId={APP_ID}
                                    objectId={card.cardId}
                                    identity={userInfo?._id}
                                    style={{ width: "100%", height: "100%" }}
                                    exposeRefApi={handleQlikApiReady}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                        {smallCards[3] && (
                          <Grid item xs={12} md={2.5}>
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: 500,
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                boxShadow: 1,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 1,
                                  pointerEvents: "none",
                                }}
                              >
                                <CircularProgress size={24} />
                              </Box>
                              <Box
                                sx={{
                                  position: "relative",
                                  zIndex: 2,
                                  height: "100%",
                                }}
                              >
                                <QlikEmbed
                                  ui="analytics/chart"
                                  appId={APP_ID}
                                  objectId={smallCards[3].cardId}
                                  identity={userInfo?._id}
                                  style={{ width: "100%", height: "100%" }}
                                  exposeRefApi={handleQlikApiReady}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      </Grid>

                      {/* Participant Status card rendered separately with proper sizing */}
                      {statusCard && (
                        <Box sx={{ mt: 2, width: "37.5%" }}>
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: 329,
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              boxShadow: 1,
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                                pointerEvents: "none",
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                            <Box
                              sx={{
                                position: "relative",
                                zIndex: 2,
                                height: "100%",
                                "& iframe": {
                                  transform: "scale(1.4)",
                                  transformOrigin: "top left",
                                  width: "71.5%",
                                  height: "71.5%",
                                },
                              }}
                            >
                              <QlikEmbed
                                ui="analytics/chart"
                                appId={APP_ID}
                                objectId={statusCard.cardId}
                                identity={userInfo?._id}
                                style={{ width: "100%", height: "100%" }}
                                exposeRefApi={handleQlikApiReady}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box key={sectionIdx} sx={{ mt: 4 }}>
                    {section.title && (
                      <Typography variant="h6" gutterBottom>
                        {section.title}
                      </Typography>
                    )}
                    <Grid container spacing={2}>
                      {visibleCards.map((card) => {
                        console.log("[Dashboard] Card:", { cardId: card.cardId, title: card.title, sectionTitle: section.title, action: card.action });
                        const titleToCheck = (card.title || section.title || "").toLowerCase();
                        const isLargeCard =
                          titleToCheck.includes("participant") &&
                          titleToCheck.includes("status");

                        return (
                        <Grid
                          item
                          key={card.cardId}
                          xs={12}
                          sm={isLargeCard ? 4.5 : 6}
                          md={isLargeCard ? 4.5 : 3}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: isLargeCard ? 329 : 200,
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              boxShadow: 1,
                              overflow: isLargeCard ? "visible" : "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                                pointerEvents: "none",
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                            <Box
                              sx={{
                                position: "relative",
                                zIndex: 2,
                                height: "100%",
                                ...(isLargeCard && {
                                  "& iframe": {
                                    transform: "scale(1.4)",
                                    transformOrigin: "top left",
                                    width: "71.5%",
                                    height: "71.5%",
                                  },
                                }),
                              }}
                            >
                              {card.action === "APPROVAL_EVE_ALERT" ? (
                                <ApprovalRequest />
                              ) : (
                                <QlikEmbed
                                  ui="analytics/chart"
                                  appId={APP_ID}
                                  objectId={card.cardId}
                                  identity={userInfo?._id}
                                  style={{ width: "100%", height: "100%" }}
                                  exposeRefApi={handleQlikApiReady}
                                />
                              )}
                            </Box>
                          </Box>
                        </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
            </>
          )}
        </Container>
      )}
    </SkeletonLoader>
  );
};

export default DashboardPage;
